/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, Link } from 'react-router-dom';

/* Helpers, constants, custom methods, internal libraries */
import { useUserContext } from "../../../contexts/userContext.js";
import { useHasPermission } from "hooks/useHasPermission.js";
import { 
  AVAILABLE_CREDITS_ROUTE, 
  USED_CREDITS_ROUTE, 
  EXPIRED_CREDITS_ROUTE, 
  TRAVEL_CREDITS_ROUTE 
} from "../../../constants/routes.js";
import { getAssociatedLang } from "helpers/general.js";
import { useLocaleContext } from "contexts/localeContext.js";
import { AATripGuideLinks } from "constants/appConstants.js";

/* Components */
import UCButton from "../../buttons";
import TravelCreditsWelcomeModal from "../../modals/travelCreditsWelcomeModal/index.js";
import { ReactComponent as DownloadIcon } from "../../../resources/images/download-02.svg";
import { ReactComponent as GreenDotIcon } from "../../../resources/images/green-dot.svg";
import { ReactComponent as BlackDotIcon } from "../../../resources/images/black-dot.svg";
import { ReactComponent as RedDotIcon } from "../../../resources/images/red-dot.svg";
import { ReactComponent as SearchIcon } from "../../../resources/images/search.svg";
import { ReactComponent as ClearIcon } from "../../../resources/images/thin-close-icon.svg";
import { ReactComponent as SettingsIcon } from "../../../resources/images/settings-icon.svg";
import { ReactComponent as AlertIcon } from '../../../resources/images/alert-triangle.svg';
import { ReactComponent as AlertCircleIcon } from '../../../resources/images/alert-circle.svg';
import TripCreditsSettingsModal from "../../modals/tripCreditsSettingsModal/index.js";
import AvailableGrid from "./availableGrid/index.js";
import UsedGrid from "./usedGrid/index.js";
import ExpiredGrid from "./expiredGrid/index.js";
import UCSearch from "../../ucsearch";
import UCSelect from "../../ucselect";
import { useTravelCreditsService } from "services/travelCreditsService.js";
import { useCompaniesService } from "services/companiesService.js";
import ConfirmActionModal from "components/modals/confirmActionModal/index.js";
import { Tooltip, OverlayTrigger } from "react-bootstrap";

/* Styles */
import styles, { expirationDateSelect } from './styles.js';
import { colors } from "../../../constants/colors";
import {storageFactory} from "factory/storageFactory";
import Popover from 'react-bootstrap/Popover';


const sessionStore = storageFactory(() => sessionStorage);
const localStore = storageFactory(() => localStorage);

const spinners = {
  "available": "availableGridSpinner",
  "used": "usedGridSpinner",
  "expired": "expiredGridSpinner",
};

const messages = defineMessages({
  searchPlaceholderMessage: {
    id: 'travelcredits.search'
  }
})

const tripCreditsOptionsText = [
  {
    key: 'TRUE',
    titleId: 'tripCredits.settings.modal.automaticAssign.title',
    descriptionId: 'tripCredits.settings.modal.automaticAssign.description'
  },
  {
    key: 'FALSE',
    titleId: 'tripCredits.settings.modal.holdCredits.title',
    descriptionId: 'tripCredits.settings.modal.holdCredits.description'
  },
  {
    key: 'PERSONAL',
    titleId: 'tripCredits.settings.modal.travelerOwned.title',
    descriptionId: 'tripCredits.settings.modal.travelerOwned.description'
  }
];

const TravelCreditsTab = ({downloadTravelCredits, tripCreditsSettings}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const userDetails = useUserContext();
  const { checkPermission } = useHasPermission();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const intl = useIntl();
  const globalLocale = useLocaleContext();

  const getTripCreditGuideLink = () => {
    switch(getAssociatedLang(globalLocale)) {
      case "EN":
        return AATripGuideLinks.EN
      case "FR":
      case "FR-CA":
        return AATripGuideLinks.FR
      case "ES":
        return AATripGuideLinks.ES
      case "PT":
        return AATripGuideLinks.PT
      case "PT-BR":
        return AATripGuideLinks.PT
      default:
        return AATripGuideLinks.EN;
    }
  }

  const { getCompanyAttributes } = useTravelCreditsService();
  const { getMembershipOptions } = useCompaniesService();

  const [activeTab, setActiveTab] = useState("");
  const [query, setQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [enableDownloadButton, setEnableDownloadButton] = useState(false);
  const [showTravelCreditsWelcomeModal, setShowTravelCreditsWelcomeModal] = useState(true);
  const [settingsModal, setSettingsModal] = useState(false);
  const [settingsPreferences, setSettingsPreferences] = useState();
  const [optionsResponse, setSettingsOptions] = useState();
  const [showTripCreditsConfirmActionModal, setShowTripCreditsConfirmActionModal] = useState(false);
  const [expirationFilter, setExpirationFilter] = useState("all");

  const isMclaCountry = sessionStore.getItem("isMclaCountry");

  const [gridOptions, setGridOptions] = useState(() => {
    const initialPage = new URLSearchParams(location.search).get('page') || 1;
    return {
      page: +initialPage,
      searchterm: ""
    };
  });

  const expirationFilterOptions = [
    { label:<FormattedMessage id="tripCredits.expirationFilter.all" />, value: "all", selectedLabel: <FormattedMessage id="tripCredits.expirationFilter.expirationAll" /> },
    { label: <FormattedMessage id="tripCredits.expirationFilter.30" />, value: "30", selectedLabel: <FormattedMessage id="tripCredits.expirationFilter.30" /> },
    { label: <FormattedMessage id="tripCredits.expirationFilter.60" />, value: "60", selectedLabel: <FormattedMessage id="tripCredits.expirationFilter.60" /> },
    { label: <FormattedMessage id="tripCredits.expirationFilter.90" />, value: "90", selectedLabel: <FormattedMessage id="tripCredits.expirationFilter.90" /> },
  ]

  useEffect(() => {
    setActiveTab(getDefaultPage())
  }, [location.pathname]);

  const fetchSettingsData = async () => {
    const [attributesResponse, optionsResponse] = await Promise.all([
      getCompanyAttributes(),
      getMembershipOptions("O_AUTOASSIGN_TRIPCREDITS"),
    ])
    if (attributesResponse) {
      setSettingsPreferences(attributesResponse)
    }

    if (optionsResponse) {
      setSettingsOptions(optionsResponse)
    }
  }

  useEffect(() => {
    fetchSettingsData()
  }, [])

  const handleConfirmModalOpen = () => {
    setShowTripCreditsConfirmActionModal(true);
  };

  const matchedOption = tripCreditsOptionsText.find(
    (item) => item.key === settingsPreferences?.currentTripCreditAutoAssignmentType?.toUpperCase()
  );

  const getConfirmDescription = () => (
    <div className={classes.confirmActionContainer}>
      <p>
        <FormattedMessage
          id="travelcredits.confirm.settings.description1"
          values={{
            assignmentSetting: matchedOption?.titleId ? (
              <span>
                <FormattedMessage id={matchedOption.titleId} />
              </span>
            ) : null,
          }}
        />
      </p>
      {settingsPreferences?.tripCreditAssignmentTimeInterval !== "0" && (
        <p>
          <FormattedMessage id="travelcredits.confirm.settings.description2" />
        </p>
      )}
      <p>
        <FormattedMessage id="travelcredits.confirm.settings.description3" />
      </p>
    </div>
  );

  const handleSettingsModalClose = (val) => {
    if (!val) fetchSettingsData();
    setSettingsModal(val);
  };

  const handleTripCreditSettings = async () => {
    const response = await tripCreditsSettings(settingsPreferences?.currentTripCreditAutoAssignmentType)
    if (response) {
      setShowTripCreditsConfirmActionModal(false)
      handleSettingsModalClose(false)
    }
  }

  const handleTabChange = (tab, route) => {
    setActiveTab(tab)
    clearSearch()
    setExpirationFilter("all")
    setGridOptions({ page: 1, searchterm: "", expiryDays: null, sortBy: null, sortDirection: null });
    navigate('../' + route);
  }

  const getDefaultPage = () => {
    if(location.pathname.includes(AVAILABLE_CREDITS_ROUTE)) return "available"
    if(location.pathname.includes(USED_CREDITS_ROUTE)) return "used"
    if(location.pathname.includes(EXPIRED_CREDITS_ROUTE)) return "expired"
    if(location.pathname.includes(TRAVEL_CREDITS_ROUTE)) return "available"
  }

  const handleDownload = () => {
    downloadTravelCredits({...gridOptions, status: activeTab})
  }

  const onChangeInput = event => {
    setInputValue(event.target.value)
    if (event.target.value === "") {
      setQuery("")
      setGridOptions({
        ...gridOptions,
        searchterm: ""
      })
    }
  }

  const clearSearch = () => {
    if (inputRef.current) {
      inputRef.current.value = ""
      setInputValue("")
      setQuery("")
      setGridOptions({
        ...gridOptions,
        searchterm: ""
      })
    }
  }

  const searchGrid = (e) => {
    if (e.key === 'Enter') {
      setQuery(e.target.value)
      setGridOptions({
        page: 1,
        searchterm: e.target.value
      })
    }
  }

  const tripCreditNumber = (parseInt(localStore.getItem("travelCreditsCount")) || 0)

  return (
    <div className={classes.travelCreditsTab}>
      <div className={classes.travelCreditsHeader}>
        <div>
          <div className={classes.travelCreditsTitleContainer}>
            <h1 className={classes.title}>
              <FormattedMessage id="travelcredits.headerTitle" />
            </h1>
            {matchedOption && (
              <OverlayTrigger
                placement="bottom-start"
                overlay={
                  <Popover className={classes.infoPopover}>
                    <div className={classes.infoPopoverDiv}>
                     <FormattedMessage id={matchedOption.descriptionId} />
                    </div>
                  </Popover>
                }
              >
                <p className={classes.blueText}>
                  <AlertCircleIcon className={classes.blueIcon} />
                  <FormattedMessage id={matchedOption.titleId} />
                </p>
              </OverlayTrigger>
            )}
          </div>
          <h3 className={classes.subtitle}>
            {isMclaCountry ?
              <>
                <FormattedMessage id="travelcredits.headerSubtitle.mclaCountry" />{" "}
                <Link to={getTripCreditGuideLink()} target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="travelcredits.headerSubtitle.tripCreditGuide" />
                </Link>
                {checkPermission("F_TRIPCREDIT_USAGE_MESSAGE") &&
                  <FormattedMessage id="travelcredits.headerSubtitle.mclaCountry2" />
                }
              </>
               :
               <FormattedMessage id="travelcredits.headerSubtitle" />
            }
          </h3>
        </div>
        <div className={classes.buttonsDiv}>
          {checkPermission("F_COMPANY_TOGGLE_AUTOASSIGN_TRIPCREDITS") && (
            <div className={classes.settingsButton}>
              <UCButton
                type="default"
                className={classes.button}
                onClick={() => setSettingsModal(true)}
                text={<FormattedMessage id="travelcredits.settings"/>}
                icon={<SettingsIcon />}
              />
            </div>
          )}
        </div>
      </div>
      {tripCreditNumber > 0 ?
        <div className={classes.expiryBanner}>
          <div className={classes.alertIcon}><AlertIcon /></div>
          {tripCreditNumber === 1 ?
            <p>
              <FormattedMessage id="tripCredit.oneExpiryBannerText" values={{ tripCreditNumber: tripCreditNumber}}/>
            </p> :
            <p>
              <FormattedMessage id="tripCredit.expiryBannerText" values={{ tripCreditNumber: tripCreditNumber}}/>
            </p>}
        </div> :
        <hr className={classes.divider} />
      }
      <div className={classes.filters}>
        <div className={classes.tabs}>
          <UCButton
            onClick={() => handleTabChange("available", AVAILABLE_CREDITS_ROUTE)}
            type="default"
            className={`${classes.tab} ${classes.availableButton} ${activeTab === "available" ? classes.active : ""}`}
            text={<FormattedMessage id="travelcredits.available" />}
            icon={<GreenDotIcon />}
            iconClassName={classes.icon}
          />
          <UCButton
            onClick={() => handleTabChange("used", USED_CREDITS_ROUTE)}
            type="default"
            className={`${classes.tab} ${classes.usedButton} ${activeTab === "used" ? classes.active : ""}`}
            text={<FormattedMessage id="travelcredits.used" />}
            icon={<BlackDotIcon />}
            iconClassName={classes.icon}
          />
          <UCButton
            onClick={() => handleTabChange("expired", EXPIRED_CREDITS_ROUTE)}
            type="default"
            className={`${classes.tab} ${classes.expiredButton} ${activeTab === "expired" ? classes.active : ""}`}
            text={<FormattedMessage id="travelcredits.expired" />}
            icon={<RedDotIcon />}
            iconClassName={classes.icon}
          />
        </div>
        <div className={classes.searchContainer}>
          <UCSearch
            className={classes.inputWrapperStyling}
            icon={<SearchIcon />}
            placeholder={intl.formatMessage(messages.searchPlaceholderMessage)}
            onChange={onChangeInput}
            onKeyDown={e => searchGrid(e)}
            autocomplete="off"
            name="searchTravelCredits"
            spinner={{
              name: query && spinners[activeTab],
              size: 20,
              color: colors.inactiveGrey,
            }}
            clearButton={
              <UCButton
                onClick={() => clearSearch()}
                className={classes.clearBtn}
                icon={<ClearIcon />}
                iconClassName={classes.clearBtnIcon}
              />
            }
            showClearButton={inputValue.length > 0}
            inputRef={inputRef}
          />
          <div className={classes.gridActionsButtons}>
            {activeTab === "available" && (
              <UCSelect
                className={classes.expirationFilter}
                styles={expirationDateSelect(theme)}
                options={expirationFilterOptions}
                value={expirationFilter}
                onChange={(selected) => {
                  const val = selected?.value === "all" ? null : selected?.value;
                  setExpirationFilter(selected?.value);
                  setGridOptions({ ...gridOptions, page: 1, expiryDays: val });
                }}
                customSelectedValue="selectedLabel"
              />
            )}
            <div className={classes.buttonContainer}>
                <UCButton
                  type="primary"
                  className={classes.button}
                  onClick={handleDownload}
                  text={<FormattedMessage id="travelcredits.download"/>}
                  icon={<DownloadIcon />}
                  disabled={!enableDownloadButton}
                  spinner={{
                    name: "downloadTravelCreditsSpinner",
                    size: 20,
                    color: colors.white
                  }}
                />
              </div>
          </div>
        </div>
      </div>
      {
        activeTab === "available" &&
        <AvailableGrid gridOptions={gridOptions} setGridOptions={setGridOptions} query={query} clearSearch={clearSearch} setEnableDownloadButton={setEnableDownloadButton} expirationFilter={gridOptions.expiryDays}/>
      }
      {
        activeTab === "used" &&
        <UsedGrid gridOptions={gridOptions} setGridOptions={setGridOptions} query={query} clearSearch={clearSearch} setEnableDownloadButton={setEnableDownloadButton} expirationFilter={gridOptions.expiryDays}/>
      }
      {
        activeTab === "expired" &&
        <ExpiredGrid gridOptions={gridOptions} setGridOptions={setGridOptions} query={query} clearSearch={clearSearch} setEnableDownloadButton={setEnableDownloadButton} expirationFilter={gridOptions.expiryDays}/>
      }
      {userDetails?.userInfo?.showTripCreditModal && checkPermission("F_VIEW_TRIPCREDIT_MODAL") && (
         <TravelCreditsWelcomeModal 
         showTravelCreditsWelcomeModal={showTravelCreditsWelcomeModal}
         setShowTravelCreditsWelcomeModal={setShowTravelCreditsWelcomeModal}
       />
     )} 
     <TripCreditsSettingsModal
        optionsResponse={optionsResponse}
        showModal={settingsModal} 
        setShowModal={handleSettingsModalClose}
        submitCallback={handleTripCreditSettings}
        settingsPreferences={settingsPreferences}
        setSettingsPreferences={setSettingsPreferences}
        tripCreditsOptionsText={tripCreditsOptionsText}
        showTripCreditsConfirmActionModal={showTripCreditsConfirmActionModal}
        handleConfirmModalOpen={handleConfirmModalOpen}
     />
      <ConfirmActionModal
        showModal={showTripCreditsConfirmActionModal}
        setShowModal={setShowTripCreditsConfirmActionModal}
        modalTitle={ <FormattedMessage id={"travelcredits.confirm.settings.title"} /> }
        modalDescription={ getConfirmDescription() }
        submitCallback={ handleTripCreditSettings }
        cancelText={ <FormattedMessage id={"travelcredits.confirm.modal.back.button"} /> }
        confirmText={ <FormattedMessage id={"travelcredits.confirm.modal.confirm.button"} /> }
        btnSpinner="tripDetailsSettingsSpinner"
      />
    </div>
  );
};

export default TravelCreditsTab;
